import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { getStudentAppointmentBookingOptions } from "../utils/student-appointment-booking-options";

interface UseStudentAppointmentBookingsGetProps {
  studentId: number | string | null | undefined;
  enabled?: boolean;
}

export function useStudentAppointmentBookingsGet({
  studentId,
  enabled = true,
}: UseStudentAppointmentBookingsGetProps) {
  const accessToken = useAccessToken();
  const numericStudentId =
    studentId != null && studentId !== "" ? Number(studentId) : 0;
  const canFetch = enabled && numericStudentId > 0 && !!accessToken;

  const {
    data: bookings,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QueryKeys.AppointmentBookings, "student", numericStudentId],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(PrivatesAppointmentsApi, accessToken);
      return service.apiAppointmentsBookingsByStudentStudentIdGet({
        studentId: numericStudentId,
      });
    },
    enabled: canFetch,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const options = useMemo(
    () => getStudentAppointmentBookingOptions(bookings),
    [bookings],
  );

  return {
    bookings: bookings ?? [],
    options,
    isPending: canFetch && isPending,
    isError,
  };
}
